import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { persistRootSubtree } from './persist-task-writes.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { getLLMRouter } from '../llm/llm-gate.js';
import { randomUUID } from 'node:crypto';
import { LLM_PAYLOAD_SAFE_LIMIT_BYTES } from '../../../shared/constants.js';
import {
  msGenerationAgentModel,
  msGenerationAgentReasoningEffort,
  msGenerationAgentTemperature,
} from '../../../shared/models.js';
import { Conversation } from '../../conversation.js';
import { Exam, MasterSolution } from '../../exam.js';
import { refreshSolutionSourceState } from '../../utils/solution-source.js';
import {
  addSolutionChangedTasks,
  dropSolutionTasks,
  markSolutionTasksDone,
  markSolutionTaskStarted,
  setSolutionStatus,
  setSolutionTotal,
  startSolutionProgress,
  StepState,
} from '../../extraction-progress.js';
import { updateSetupStep } from '../pending-tasks/extraction-or-generation-finished.js';
import { buildMasterSolutionPrompts } from '../../prompts.js';
// Imported directly from the tool module (not the tools barrel) to avoid the barrel's
// grading-scheme.tool → gs-generation-agent import cycle. memory.tool has no back-import here.
import { getTeacherMemoryInstructionBlock } from '../tools/memory.tool.js';
import { appendTaskRefSegment, normalizeTaskRefs, normalizeTaskRefSegment } from '../../utils/task-ref.js';
import { addCostToExam } from '../add-cost.js';
import {
  type AgentPayloadPart,
  assembleHierarchyPayload,
  clampPayloadToByteLimit,
  imageIdsAsBoxes,
  type HierarchyTaskInput,
  type PayloadBox,
} from './agent-payload.js';
import {
  collectAllTaskAdditionalInfoIds,
  normalizeAdditionalInfoForPrompt,
  resolveAdditionalInfoForTask,
} from './additional-info-context.js';
import {
  buildPathMap,
  collectSubtree,
  collectSubtreeTaskTypes,
  getTaskKey,
  gradableSubtreeNodes,
  hasMasterSolutionContent,
  isGradable,
  msTargets,
  solutionUnitNodeCount,
  solutionUnitRoots,
  type NestedExamTask,
  type SubtreeNode,
} from './tree-generation-utils.js';

/** One master solution per gradable task in the subtree, keyed by the task header id (path). */
export interface MsResultEntry {
  id: string;
  content: string;
}

async function generateAiMasterSolution(
  systemPrompt: string,
  userPrompt: string,
  materialParts: AgentPayloadPart[] = [],
  examDocId?: string,
): Promise<MsResultEntry[]> {
  const router = getLLMRouter();

  try {
    const TIMEOUT_MS = 300000; // 5 minutes
    const timeoutPromise = new Promise<MsResultEntry[]>((_, reject) => {
      setTimeout(() => {
        reject(new Error('TIMEOUT'));
      }, TIMEOUT_MS);
    });

    // The context (task description, additional information, and — when refining — the current master
    // solution) is supplied as labeled, interleaved parts in the user message. Resize the images for
    // this single call if their combined size exceeds the OpenRouter limit.
    const parts = await clampPayloadToByteLimit(materialParts, LLM_PAYLOAD_SAFE_LIMIT_BYTES);

    const userContentParts: any[] = [{ type: 'text', text: userPrompt }, ...parts];

    const responsePromise = router.chat.completions.create({
      stream: false,
      model: msGenerationAgentModel,
      provider: 'openrouter',
      temperature: msGenerationAgentTemperature,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userContentParts },
      ],
      response_format: { type: 'json_object' } as any,
      // OpenRouter reasoning control — forwarded verbatim by the router (token.js does not whitelist params).
      ...({ reasoning: { effort: msGenerationAgentReasoningEffort } } as any),
    });

    responsePromise.catch(err => {
      logger.warn(err, 'Background responsePromise rejected after timeout race concluded.');
    });

    let response;
    try {
      response = await Promise.race([responsePromise, timeoutPromise]);
    } catch (error: any) {
      if (error?.message === 'TIMEOUT') {
        logger.warn(error, `Timeout bei der Generierung der Musterlösung.`);
        return [];
      }
      throw error;
    }

    const raw = response.choices[0]?.message?.content;
    const rawContent = typeof raw === 'string' ? raw : (raw?.content ?? '');

    // Track cost for this API call
    await addCostToExam(response, examDocId, 'msGen');
    logger.info(
      { cost: JSON.stringify(response?.usage?.cost), context: 'COST_TRACKING' },
      'MS Generation Agent cost tracked',
    );

    if (!rawContent) {
      logger.warn({ context: 'MSGen' }, 'LLM returned empty content for Musterloesung generation');
      return [];
    }

    const parsedData = typeof rawContent === 'string' ? JSON.parse(rawContent) : rawContent;
    const results: MsResultEntry[] = Array.isArray(parsedData?.results)
      ? parsedData.results
          .filter((r: any) => r && r.id != null)
          .map((r: any) => ({ id: String(r.id), content: String(r.content ?? '') }))
      : [];

    return results;
  } catch (error) {
    logger.error(error, `LLM Aufruf fehlgeschlagen:`);
    throw error;
  }
}

interface MsHierarchyBuild {
  subtree: SubtreeNode[];
  targets: SubtreeNode[];
  targetIds: Set<string>;
  targetTaskIds: string[];
  hierarchyInputs: HierarchyTaskInput[];
}

/**
 * Builds the per-node hierarchy inputs (task description + additional info + current MS-when-refining)
 * for one root subtree — the exact material a generation call for this root sends. Extracted out of
 * `generateMasterSolutionForTree` so run + refine share one implementation.
 */
function buildMsHierarchyInputs(
  ctx: MsTreeContext,
  root: NestedExamTask,
  opts: { feedback?: string; targetKeys?: Set<string> } = {},
): MsHierarchyBuild {
  const { allAdditionalInfo, allTaskAdditionalInfoIds } = ctx;
  const isRefine = !!opts.feedback;
  const subtree = collectSubtree(root);
  const targets = subtree.filter(s => {
    if (!isGradable(s.node)) return false;
    if (opts.targetKeys && !opts.targetKeys.has(getTaskKey(s.node))) return false;
    return isRefine || !hasMasterSolutionContent(s.node);
  });
  const targetIds = new Set(targets.map(s => s.id));
  // Real task `_id`s (getTaskKey), not the path-based `targetIds` above (that one only correlates the
  // LLM's response back to a node) — this is the actual SolutionProgress unit, see its doc comment.
  const targetTaskIds = targets.map(s => getTaskKey(s.node));

  const hierarchyInputs: HierarchyTaskInput[] = subtree.map(s => {
    const node = s.node;
    const info = resolveAdditionalInfoForTask(node, allAdditionalInfo, allTaskAdditionalInfoIds);
    const msNode = ((node as any).masterSolution?.doc ?? (node as any).masterSolution) || {};
    const isTarget = targetIds.has(s.id);
    const entry: HierarchyTaskInput = {
      id: s.id,
      hierarchy: isTarget ? 'TARGET' : 'CONTEXT',
      taskType: typeof (node as any).taskType === 'string' ? (node as any).taskType : undefined,
      taskDescription: { boxes: (node as any).boundingBoxes as PayloadBox[] | undefined, text: node.description },
      additionalInformation:
        info.length > 0
          ? {
              boxes: info.flatMap(i => imageIdsAsBoxes(i.imageIds)),
              text: info
                .map(i => i.content)
                .filter(Boolean)
                .join('\n\n'),
            }
          : undefined,
    };
    if (isRefine && msNode.content) {
      entry.masterSolution = { boxes: msNode.boundingBoxes as PayloadBox[] | undefined, text: msNode.content };
      entry.current = true;
    }
    return entry;
  });

  return { subtree, targets, targetIds, targetTaskIds, hierarchyInputs };
}

interface MsTreeContext {
  examDocId: string;
  examContext: { subject?: string; topic?: string; classLevel?: string };
  teacherMsRules: string;
  userLanguage: string;
  allAdditionalInfo: ReturnType<typeof normalizeAdditionalInfoForPrompt>;
  allTaskAdditionalInfoIds: ReturnType<typeof collectAllTaskAdditionalInfoIds>;
  inFlight: Set<string>;
}

/**
 * Generate master solutions for a whole main-task subtree in one LLM call, so the model produces
 * coherent solutions across subtasks. `opts.feedback` switches to refine mode (regenerate every
 * gradable node in the subtree using its current solution + feedback). Shared by run + refine.
 */
async function generateMasterSolutionForTree(
  ctx: MsTreeContext,
  root: NestedExamTask,
  // `targetKeys` (refine only) restricts which gradable nodes are regenerated — used to refine just the
  // requested subtask subtree while the rest of the root stays as read-only CONTEXT.
  opts: { feedback?: string; targetKeys?: Set<string> } = {},
): Promise<{ refinedRefs: string[]; contentParts: string[] }> {
  const empty = { refinedRefs: [] as string[], contentParts: [] as string[] };
  const { examDocId, examContext, teacherMsRules, userLanguage, inFlight } = ctx;
  const rootKey = getTaskKey(root);
  if (inFlight.has(rootKey)) return empty;
  inFlight.add(rootKey);
  try {
    const isRefine = !!opts.feedback;
    const { targets, targetIds, targetTaskIds, hierarchyInputs } = buildMsHierarchyInputs(ctx, root, opts);
    if (targets.length === 0) return empty;

    const materialParts = await clampPayloadToByteLimit(
      await assembleHierarchyPayload(hierarchyInputs),
      LLM_PAYLOAD_SAFE_LIMIT_BYTES,
    );

    const { systemPrompt, userPrompt } = buildMasterSolutionPrompts(
      {
        examContext,
        taskContext: { taskNumber: root.taskNumber, taskTypes: collectSubtreeTaskTypes(root) as any },
        feedback: opts.feedback,
        teacherRules: teacherMsRules,
      },
      userLanguage,
    );

    try {
      await markSolutionTaskStarted(examDocId, 'masterSolution', targetTaskIds);
    } catch (e) {
      logger.error({ err: e, rootKey }, 'Failed to mark MS nodes as startedForTasks');
    }

    logger.info({ taskNumber: root.taskNumber, examDocId, isRefine }, 'Generiere Musterlösung für Aufgabenbaum');
    const results = await generateAiMasterSolution(systemPrompt, userPrompt, materialParts, examDocId);

    const pathMap = buildPathMap(root);
    const contentParts: string[] = [];
    for (const r of results) {
      if (!targetIds.has(r.id)) continue; // ignore CONTEXT / unknown ids
      const node = pathMap.get(r.id);
      if (!node) continue;
      const existing = ((node as any).masterSolution?.doc ?? (node as any).masterSolution) || {};
      const masterSolution = new MasterSolution({
        ...(isRefine ? existing : {}),
        _id: existing._id || randomUUID(),
        examId: examDocId,
        taskNumber: node.taskNumber,
        content: r.content,
        // This agent wrote the content — on a refine too, which replaces what was there before.
        source: 'generated' as const,
        feedback: isRefine ? [...(Array.isArray(existing.feedback) ? existing.feedback : []), opts.feedback] : [],
      });
      (node as any).masterSolution = masterSolution.toObject();
      contentParts.push(`Aufgabe ${node.taskNumber}:\n${r.content}`);
    }

    try {
      // Writes THIS root only, leaving its siblings alone — which is what lets two main tasks generate at the
      // same time. It used to read the whole `tasks` array, swap this subtree into a copy and write all of it
      // back, so a concurrent root's work was lost outright and every root had to serialise.
      const changedTaskIds = await persistRootSubtree(String(examDocId), root, 'masterSolution');
      // Same diff the upload path records — a generation that filled gaps changed exactly these.
      if (changedTaskIds.length > 0) await addSolutionChangedTasks(String(examDocId), 'masterSolution', changedTaskIds);
      // Keep the exam-state provenance roll-up in step with the tasks this subtree just changed, so the
      // status bar reflects a mix as soon as the first generated solution lands next to an uploaded one.
      await refreshSolutionSourceState(String(examDocId));
      // One call covers one unit: this root.
      await markSolutionTasksDone(examDocId, 'masterSolution', targetTaskIds);
    } catch (e) {
      logger.error({ err: e, rootKey }, 'Failed to persist master solutions for subtree');
    }

    return { refinedRefs: targets.map(s => s.id), contentParts };
  } catch (innerGenError) {
    logger.error(
      { err: innerGenError, rootKey, context: 'generateMasterSolutionForTree' },
      'Failed to compute master solution subtree gracefully',
    );
    return empty;
  } finally {
    inFlight.delete(rootKey);
  }
}

/**
 * Everything a tree generation needs that does NOT vary by root — one teacher-memory read and one
 * conversation read. Extracted so that the per-root entry point below can build, for a single root, the
 * same context `runMasterSolutionAgent` builds once for all of them.
 */
async function buildMsTreeContext(exam: any): Promise<MsTreeContext> {
  // Teacher's stored master-solution preferences (subject-filtered), loaded once and injected into
  // every per-task prompt.
  const teacherMsRules = await getTeacherMemoryInstructionBlock({
    userID: exam?.userID,
    category: 'master_solution_design',
    subject: exam?.subject,
  });

  let userLanguage = 'English';
  try {
    const conversationCollection = await getCollection(Conversation);
    const conversation = await conversationCollection.findOne({ examId: String(exam?._id) });
    if (conversation && (conversation as any).language) {
      userLanguage = (conversation as any).language;
    }
  } catch (convErr) {
    logger.error(
      { err: convErr, examId: String(exam?._id), context: 'generateMS' },
      'Failed to fetch conversation for exam',
    );
  }

  return {
    examDocId: exam._id,
    examContext: {
      subject: exam?.subject || 'Nicht Angegeben',
      topic: exam?.topic || 'Nicht Angegeben',
      classLevel: exam?.class || 'Nicht Angegeben',
    },
    teacherMsRules,
    userLanguage,
    allAdditionalInfo: normalizeAdditionalInfoForPrompt((exam as any)?.additionalInformation),
    allTaskAdditionalInfoIds: collectAllTaskAdditionalInfoIds(exam.tasks as NestedExamTask[] | undefined),
    inFlight: new Set<string>(),
  };
}

/** Why a per-root generation did nothing. Both outcomes are normal, neither is a failure. */
export type MsRootSkip = 'root-gone' | 'already-covered';

/**
 * Generates the master solution for ONE top-level main task.
 *
 * `runMasterSolutionAgent` already splits its work exactly this way internally — one LLM call per root
 * subtree, so the model can be coherent across subtasks — and this makes a single root ADDRESSABLE, so each
 * can be claimed, retried and (across instances) parallelised on its own.
 *
 * It deliberately does none of that function's run-level bookkeeping: it does not open the progress run, does
 * not subscribe for roots that appear later, and does not decide the run is finished. Those are properties of
 * the whole SET of roots, and with one job per root there is no longer a single place in the process that owns
 * the set — the exam state is that place, and the job definition reads it.
 */
export async function generateMasterSolutionForRootTask(
  examId: string,
  taskKey: string,
): Promise<{ generated: boolean; skipped?: MsRootSkip }> {
  const examCollection = await getCollection(Exam);
  const exam = await examCollection.findOne({ _id: examId });
  if (!exam) throw new Error(`Exam mit ID ${examId} nicht gefunden.`);

  const root = ((exam.tasks || []) as NestedExamTask[]).find(task => getTaskKey(task) === taskKey);
  // A root vanishing between enqueue and claim is a normal race, not a failure: a structure rebuild
  // replaces the tree wholesale, and the reconcile that follows creates jobs for whatever replaced it.
  if (!root) return { generated: false, skipped: 'root-gone' };
  // Re-checked here and not only in `ready()`, because the two are separated by the claim: another run, a
  // re-upload extraction, or the teacher can fill this subtree in between.
  if (msTargets(root).length === 0) return { generated: false, skipped: 'already-covered' };

  const ctx = await buildMsTreeContext(exam);
  await generateMasterSolutionForTree(ctx, root, {});
  return { generated: true };
}

/**
 * Opens a master-solution GENERATION run without doing any of it.
 *
 * This is the whole of what the `generate_master_solution` tool now does, and it is deliberately just a state
 * write: `msGeneration.desired()` keys off exactly this (`producer: 'generation'` + IN_PROGRESS) to derive one
 * job per outstanding root, so opening the run IS requesting the work. The teacher's intent becomes a fact
 * about the exam rather than a function call held in one process, which is what lets a restart, a deploy or a
 * second instance pick it up.
 */
export async function openMasterSolutionGenerationRun(examId: string): Promise<number> {
  const examCollection = await getCollection(Exam);
  const exam = await examCollection.findOne({ _id: examId });
  if (!exam) throw new Error(`Exam mit ID ${examId} nicht gefunden.`);

  const roots = solutionUnitRoots(exam.tasks as NestedExamTask[]);
  // Progress first, rows second: this call is what makes the step read as IN_PROGRESS, and a row that finished before
  // it landed would close a step the record still called NOT_STARTED. `beginProgressRun` is NOT called here — the
  // dispatch owns it now (`onStepStart`, which also declares `reupload: false`), and calling it twice would re-clear
  // the pass fields this run has already started writing.
  await startSolutionProgress(exam._id, 'masterSolution', {
    producer: 'generation',
    total: solutionUnitNodeCount(exam.tasks as NestedExamTask[]),
    message: 'Starte Musterlösungsgenerierung...',
  });
  logger.info({ examId, roots: roots.length }, 'Opened master solution generation run');
  return roots.length;
}

/**
 * The whole run in one process: opens it, generates every root, and watches the exam for roots that arrive
 * later. Superseded by `openMasterSolutionGenerationRun` + the `msGeneration` job type, which does the same
 * thing durably; kept because `refineMasterSolutionAgent` and the direct tool path still reach for it, and
 * removing it is a separate change from wiring the jobs.
 */
export async function runMasterSolutionAgent(examId: string): Promise<boolean> {
  try {
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });

    if (!exam) {
      throw new Error(`Exam mit ID ${examId} nicht gefunden.`);
    }
    const examDocId = exam._id;

    // Opens the run without touching the task lists: this agent only fills what is still missing, so a
    // partially uploaded or previously generated solution keeps the units it already has.
    await startSolutionProgress(examDocId, 'masterSolution', {
      producer: 'generation',
      total: solutionUnitNodeCount(exam.tasks as NestedExamTask[]),
      message: 'Starte Musterlösungsgenerierung...',
    });

    logger.info({ examId }, 'Starte Master Solution Generierung fuer Exam');

    const ctx = await buildMsTreeContext(exam);
    const inFlightTaskKeys = ctx.inFlight;
    const generateForTree = (root: NestedExamTask, opts: { feedback?: string } = {}) =>
      generateMasterSolutionForTree(ctx, root, opts);

    // Generate one whole subtree per top-level main task (fills any gradable node still missing an MS).
    const rootsInitial = (exam.tasks || []) as NestedExamTask[];
    for (const root of rootsInitial) {
      if (inFlightTaskKeys.has(getTaskKey(root))) continue;
      if (msTargets(root).length === 0) continue;
      await generateForTree(root);
    }

    // Subscribe to exam changes and react to newly created tasks (no polling)
    const sub = watchQuery(
      Exam._collectionName,
      { _id: examId },
      { limit: 1, projection: { examState: 1, tasks: 1 } },
    ).subscribe({
      next: rows => {
        void (async () => {
          try {
            const doc = Array.isArray(rows) ? (rows[0] as any) : rows;
            if (!doc) {
              return;
            }

            const roots = (doc.tasks || []) as NestedExamTask[];

            try {
              await setSolutionTotal(examDocId, 'masterSolution', solutionUnitNodeCount(roots));
            } catch (e) {
              logger.error({ err: e }, 'Failed to keep the MS unit total in sync');
            }

            // Start each main-task subtree that still has a gradable node without a master solution.
            const rootsToStart = roots.filter(
              root => !inFlightTaskKeys.has(getTaskKey(root)) && msTargets(root).length > 0,
            );

            for (const root of rootsToStart) {
              try {
                await generateForTree(root);
              } catch (e) {
                logger.error({ err: e, taskKey: getTaskKey(root) }, 'Error generating MS subtree from subscription');
              }
            }

            // if task extraction completed and there are no remaining tasks to start, mark completed and unsubscribe
            const tExtStatus = doc.examState?.taskExtractionProgress?.status;
            if (tExtStatus !== StepState.COMPLETED) {
              return;
            }

            const remainingNotCreated = roots.filter(root => msTargets(root).length > 0);
            if (tExtStatus === StepState.COMPLETED && remainingNotCreated.length === 0 && inFlightTaskKeys.size === 0) {
              try {
                await setSolutionStatus(
                  examDocId,
                  'masterSolution',
                  StepState.COMPLETED,
                  'Musterlösung erfolgreich generiert',
                );
              } catch (e) {
                logger.error(e, `Failed to set MS progress to COMPLETED`);
              }
              // Generation done — surface it to the agent (gated/batched inside updateSetupStep).
              await updateSetupStep(examDocId, { masterSolution: { type: 'generation', reupload: false } });
              sub.unsubscribe();
            }
          } catch (subscriptionLoopError) {
            logger.error(
              subscriptionLoopError,
              `Critical background execution failure caught inside change stream iteration`,
            );
          }
        })();
      },
      error: err => {
        logger.error(err, `watchQuery subscription error for MS agent:`);
      },
    });

    return true;
  } catch (error) {
    logger.error(error, `Fehler im Master Solution Agent:`);
    try {
      await setSolutionStatus(examId, 'masterSolution', StepState.ERROR, 'Ein Fehler ist aufgetreten');
    } catch (e) {
      logger.error(e, `Failed to update exam state after MS agent error:`);
    }
    return false;
  }
}

export async function refineMasterSolutionAgent(
  examId: string,
  taskNumbers: string[],
  feedback: string,
): Promise<{ success: boolean; newContent?: string; error?: string; refinedTaskRefs?: string[] }> {
  logger.info(
    { examId, taskNumbers: JSON.stringify(taskNumbers), feedback },
    'refineMasterSolutionAgent started with parameters',
  );

  try {
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });

    if (!exam?.tasks) {
      return { success: false, error: 'Klausur oder Aufgaben nicht gefunden.' };
    }

    let userLanguage = 'English';
    if (examId) {
      try {
        const conversationCollection = await getCollection(Conversation);
        const conversation = await conversationCollection.findOne({ examId });
        if (conversation && (conversation as any).language) {
          userLanguage = (conversation as any).language;
        }
      } catch (convErr) {
        logger.error(
          { err: convErr, examId: String(examId), context: 'generateGS' },
          'Failed to fetch conversation for exam',
        );
      }
    }

    const allAdditionalInfo = normalizeAdditionalInfoForPrompt((exam as any)?.additionalInformation);
    const searchTaskNums = normalizeTaskRefs(taskNumbers);

    if (searchTaskNums.length === 0) {
      return { success: false, error: 'Keine gültige Aufgabennummer übergeben.' };
    }

    // Teacher's stored master-solution preferences (subject-filtered), injected into the refine prompt.
    const teacherMsRules = await getTeacherMemoryInstructionBlock({
      userID: exam?.userID,
      category: 'master_solution_design',
      subject: exam?.subject,
    });

    // Resolve each requested task to its top-level main task (root). The whole root is sent for context,
    // but only the gradable nodes of the requested subtree(s) are regenerated (collected as targetKeys).
    const findWithRoot = (
      tasks: NestedExamTask[],
      searchTaskNum: string,
      rootTask: NestedExamTask | null,
      parentTaskPath = '',
    ): { root: NestedExamTask; node: NestedExamTask; matchedRef: string } | null => {
      for (const t of tasks) {
        const currentTaskNum = normalizeTaskRefSegment(t.taskNumber);
        const currentTaskPath = appendTaskRefSegment(parentTaskPath, currentTaskNum);
        const effectiveRoot = rootTask ?? t;
        if (currentTaskNum === searchTaskNum || currentTaskPath === searchTaskNum) {
          return { root: effectiveRoot, node: t, matchedRef: currentTaskPath || currentTaskNum };
        }
        if (t.subTasks) {
          const found = findWithRoot(t.subTasks, searchTaskNum, effectiveRoot, currentTaskPath);
          if (found) return found;
        }
      }
      return null;
    };

    const allTaskAdditionalInfoIds = collectAllTaskAdditionalInfoIds(exam.tasks as NestedExamTask[] | undefined);

    const affectedRoots = new Map<string, NestedExamTask>();
    const targetKeysByRoot = new Map<string, Set<string>>();
    const resolvedRefs: string[] = [];
    const notFoundTaskNums: string[] = [];
    for (const searchTaskNum of searchTaskNums) {
      const found = findWithRoot(exam.tasks as NestedExamTask[], searchTaskNum, null);
      if (!found) {
        notFoundTaskNums.push(searchTaskNum);
        continue;
      }
      resolvedRefs.push(found.matchedRef);
      const rootKey = getTaskKey(found.root);
      affectedRoots.set(rootKey, found.root);
      // Per-node keys — the actual progress unit, now that it's the node rather than the root (see
      // SolutionProgress's doc comment). Grouped by root only because `generateMasterSolutionForTree`
      // below is still called once per root.
      const keys = targetKeysByRoot.get(rootKey) ?? new Set<string>();
      for (const s of gradableSubtreeNodes(found.node)) keys.add(getTaskKey(s.node));
      targetKeysByRoot.set(rootKey, keys);
    }

    if (notFoundTaskNums.length > 0) {
      return { success: false, error: `Aufgabe(n) nicht gefunden: ${notFoundTaskNums.join(', ')}` };
    }

    // Reset progress: drop exactly the nodes being redone (not their untouched siblings under the same
    // root) so they count as outstanding again; the total is the exam's unit count either way. Provenance
    // is per task (`masterSolution.source`, rolled up into masterSolutionSourceState) and the write-back
    // below sets it — nothing exam-level to flip here.
    await dropSolutionTasks(exam._id, 'masterSolution', [...targetKeysByRoot.values()].flatMap(keys => [...keys]));
    // `refine`, not `generation`: this is a correction to an artifact that already exists, and the setup-step
    // announcement is only for a delivery — see `SolutionProducer`. The teacher asked for this in chat and is told
    // about it in the same turn.
    await startSolutionProgress(exam._id, 'masterSolution', {
      producer: 'refine',
      total: solutionUnitNodeCount(exam.tasks as NestedExamTask[]),
      message: 'Musterlösung wird überarbeitet...',
    });

    const ctx: MsTreeContext = {
      examDocId: exam._id,
      examContext: {
        subject: exam?.subject || 'Nicht Angegeben',
        topic: exam?.topic || 'Nicht Angegeben',
        classLevel: exam?.class || 'Nicht Angegeben',
      },
      teacherMsRules,
      userLanguage,
      allAdditionalInfo,
      allTaskAdditionalInfoIds,
      inFlight: new Set<string>(),
    };

    const refinedContents: string[] = [];
    for (const [rootKey, root] of affectedRoots) {
      const res = await generateMasterSolutionForTree(ctx, root, {
        feedback,
        targetKeys: targetKeysByRoot.get(rootKey),
      });
      refinedContents.push(...res.contentParts);
    }

    await setSolutionStatus(exam._id, 'masterSolution', StepState.COMPLETED, 'Musterlösung erfolgreich überarbeitet');

    logger.info(
      { examId: exam._id, refs: resolvedRefs.join(', '), context: 'MS_REFINE' },
      'Saved and persisted refined master solutions',
    );

    if (refinedContents.length === 0) {
      logger.warn(
        { context: 'MS_REFINE' },
        'Short-circuiting automatic scheme refinement cascade since no master solutions were compiled',
      );
      return { success: false, error: 'Die Überarbeitung der Musterlösungen ist fehlgeschlagen.' };
    }

    // The downstream cascade (refine the grading scheme to match, then regrade) is fired by the caller
    // (the refine_master_solution tool), not here; we just hand back which task refs were refined.
    const combinedContent =
      refinedContents.length === 1
        ? (refinedContents[0] ?? '').replace(/^Aufgabe\s+[^:]+:\n/, '')
        : refinedContents.join('\n\n');

    return { success: true, newContent: combinedContent, refinedTaskRefs: resolvedRefs };
  } catch (error) {
    logger.error(error, `Fehler beim Refine Master Solution Agent:`);
    return { success: false, error: 'Interner Serverfehler.' };
  }
}
